// ** React Imports
import { Fragment, useState, useEffect } from 'react'

// ** Third Party Components
import Select from 'react-select'
import { useForm, Controller } from 'react-hook-form'
import { ArrowLeft, ArrowRight } from 'react-feather'
import { useOnboarding } from '../OnboardingContext'
import ProfilePictureUploader from '../ProfilePictureUploader'
import { languageOptions, dateFormatOptions, numberFormatOptions } from '../Options'
import { useTranslation } from 'react-i18next'

// ** Utils
import { selectThemeColors } from '@utils'

// ** Reactstrap Imports
import { Label, Row, Col, Button, Form, Input, FormFeedback } from 'reactstrap'

// ** Styles
import '@styles/react/libs/react-select/_react-select.scss'

const PersonalInfo = ({ stepper }) => {
  const {
    userFirstName,
    setUserFirstName,
    userLastName,
    setUserLastName,
    userEmail,
    setUserEmail,
    userPassword,
    setUserPassword,
    userLanguage,
    setUserLanguage,
    userDateFormat,
    setUserDateFormat,
    userNumberFormat,
    setUserNumberFormat,
    userProfilePicture,
    setUserProfilePicture,
    language, numberFormat, dateFormat,
    updateLatestStep
  } = useOnboarding()

  const userInfo = {
    userFirstName,
    userLastName,
    userEmail,
    userPassword,
    userLanguage,
    userDateFormat,
    userNumberFormat,
    userProfilePicture
  }

  const [isSubmittable, setIsSubmittable] = useState(false)
  const [passwordRef, setPasswordRef] = useState('')
  const { t } = useTranslation()
  
  const checkPasswords = () => {
    const strongRegex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})")
    return userPassword === passwordRef && strongRegex.test(userPassword)
  }
  
  // ** Hooks
  const {
    control,
    setError,
    handleSubmit,
    formState: { errors }
  } = useForm({userInfo})

  useEffect(() => {
    const data = Object.fromEntries(Object.entries(userInfo).filter(([key]) => !key.includes('userProfilePicture')))
    const canSubmit = Object.values(data).filter(v => typeof v !== "function" && v === "").length === 0 && checkPasswords()
    setIsSubmittable(canSubmit)
  }, [userInfo])

  const onSubmit = data => {
    const dataToVerify = Object.fromEntries(Object.entries(userInfo).filter(([key]) => !key.includes('userProfilePicture')))
    if (Object.values(dataToVerify).every(field => typeof field !== "string" || field.length > 0)) {
      updateLatestStep(4)
      stepper.next()
    } else {
      for (const key in data) {
        if (data[key] !== undefined && data[key].length === 0) {
          setError(key, {
            type: 'manual',
            message: `Please enter a valid ${key}`
          })
        }
      }
    }
  }

  const updatePP = (pp) => {
    const reader = new FileReader()
    reader.onload = event => {
      const img = event.target.result
      setUserProfilePicture(img)
    }
    reader.readAsDataURL(pp)
  }

  return (
    <Fragment>
      <div className='content-header'>
        <h5 className='mb-0'>{t('personal-info-title')}</h5>
        <small>{t('personal-info-subtitle')}</small>
      </div>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col md='2'>
            <ProfilePictureUploader title={t('chose-profile-picture')} pp={userProfilePicture} setPP={updatePP} />
          </Col>
          <Col>
        <Row>
          <Col md='4' className='mb-1'>
            <Label className='form-label' for='firstName'>
              {t('firstname')}
            </Label>
            <Controller
              id='firstName'
              name='firstName'
              control={control}
              render={({ field }) => <Input placeholder='John' invalid={errors.firstName && true} {...field} value={userFirstName} onChange={e => setUserFirstName(e.target.value)} />}
            />
            {errors.firstName && <FormFeedback>{errors.firstName.message}</FormFeedback>}
          </Col>
          <Col md='4' className='mb-1'>
            <Label className='form-label' for='lastName'>
              {t('lastname')}
            </Label>
            <Controller
              id='lastName'
              name='lastName'
              control={control}
              render={({ field }) => <Input placeholder='Doe' invalid={errors.lastName && true} {...field} value={userLastName} onChange={e => setUserLastName(e.target.value)} />}
            />
            {errors.lastName && <FormFeedback>{errors.lastName.message}</FormFeedback>}
          </Col>
          <Col md='4' className='mb-1'>
            <Label className='form-label' for={`email`}>
              {t('email')}
            </Label>
            <Controller
              id={`email`}
              name={`email`}
              control={control}
              render={({ field }) => (<Input type='email' placeholder='john.doe@gmail.com' invalid={errors[`email`] && true} {...field} value={userEmail} onChange={e => setUserEmail(e.target.value)} />)}
            />
            {errors[`email`] && (
              <FormFeedback>{errors[`email`].message}</FormFeedback>
            )}
          </Col>
          <Col md='6' className='mb-1'>
            <Label className='form-label' for='password'>
              {t('password')}
            </Label>
            <Controller
              id='password'
              name='password'
              control={control}
              render={({ field }) => <Input type='password' placeholder='********' invalid={!checkPasswords()} {...field} onChange={e => setPasswordRef(e.target.value)} />}
            />
            {!checkPasswords() || <FormFeedback>Passwords does not match</FormFeedback>}
          </Col>
          <Col md='6'>
            <Label className='form-label' for='verify-password'>
              {t('check-password')}
            </Label>
            <Controller
              id='verify-password'
              name='verify-password'
              control={control}
              render={({ field }) => <Input type='password' placeholder='********' invalid={!checkPasswords()} {...field} value={userPassword} onChange={e => setUserPassword(e.target.value)} />}
            />
            {checkPasswords() && <FormFeedback>Passwords does not match</FormFeedback>}
          </Col>
          <Col md="12" className='mb-1'>
            <Label className='form-label' for='verify-password'>
              {t('password-constraint')}
            </Label>
          </Col>
        </Row>
        <Row>
          
        </Row>
        <Row>
          {/* <Col md='6' className='mb-1'>
            <Label className='form-label' for='country'>
              {t('country')}
            </Label>
            <Select
              theme={selectThemeColors}
              isClearable={false}
              id={`country`}
              className='react-select'
              classNamePrefix='select'
              options={countryOptions}
              value={countryOptions.find(option => option.value === (userCountry ? userCountry : country))} 
              onChange={e => setUserCountry(e.value)}
            />
          </Col> */}
          <Col md='4' className='mb-1'>
            <Label className='form-label' for='language'>
            {t('language')}
            </Label>
            <Select
              isClearable={false}
              theme={selectThemeColors}
              id={`language`}
              options={languageOptions}
              className='react-select'
              classNamePrefix='select'
              value={languageOptions.find(option => option.value === (userLanguage ? userLanguage : language))} 
              onChange={e => setUserLanguage(e.value)}
            />
          </Col>
          <Col md='4' className='mb-1'>
            <Label className='form-label' for='dateFormat'>
            {t('date-format')}
            </Label>
            <Select
              theme={selectThemeColors}
              isClearable={false}
              id={`dateFormat`}
              className='react-select'
              classNamePrefix='select'
              options={dateFormatOptions}
              onChange={e => setUserDateFormat(e.value)}
              value={dateFormatOptions.find(option => option.value === (userDateFormat ? userDateFormat : dateFormat))} 
            />
          </Col>
          <Col md='4' className='mb-1'>
            <Label className='form-label' for='numberFormat'>
            {t('number-format')}
            </Label>
            <Select
              isClearable={false}
              theme={selectThemeColors}
              id={`numberFormat`}
              options={numberFormatOptions}
              className='react-select'
              classNamePrefix='select'
              value={numberFormatOptions.find(option => option.value === (userNumberFormat ? userNumberFormat : numberFormat))} 
              onChange={e => setUserNumberFormat(e.value)}
            />
          </Col>
        </Row>
          </Col>
        </Row>
        <div className='d-flex justify-content-between'>
          <Button type='button' color='primary' className='btn-prev' onClick={() => stepper.previous()}>
            <ArrowLeft size={14} className='align-middle me-sm-25 me-0'></ArrowLeft>
            <span className='align-middle d-sm-inline-block d-none'>{t('previous')}</span>
          </Button>
          <Button type='submit' color='primary' className='btn-next' disabled={!isSubmittable}>
            <span className='align-middle d-sm-inline-block d-none'>{t('next')}</span>
            <ArrowRight size={14} className='align-middle ms-sm-25 ms-0'></ArrowRight>
          </Button>
        </div>
      </Form>
    </Fragment>
  )
}

export default PersonalInfo
